<template>
  <div class="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
    <div class="max-w-md mx-auto">
      <!-- Header -->
      <div class="text-center mb-8">
        <h1 class="text-3xl font-bold text-gray-800 mb-2">Lead Capture</h1>
        <p class="text-gray-600">Please provide your information below</p>
      </div>
      
      <!-- Lead Form -->
      <form @submit.prevent="submitLead" class="bg-white rounded-xl shadow-lg p-6 space-y-6">
        <!-- Name Field -->
        <div>
          <label for="name" class="block text-lg font-medium text-gray-700 mb-2">
            Name *
          </label>
          <input
            id="name"
            v-model="form.name"
            type="text"
            required
            class="form-input"
            placeholder="Enter your full name"
            :disabled="isLoading"
          />
        </div>
        
        <!-- Email Field -->
        <div>
          <label for="email" class="block text-lg font-medium text-gray-700 mb-2">
            Email *
          </label>
          <input
            id="email"
            v-model="form.email"
            type="email"
            required
            class="form-input"
            placeholder="Enter your email address"
            :disabled="isLoading"
          />
        </div>
        
        <!-- Company Field -->
        <div>
          <label for="company" class="block text-lg font-medium text-gray-700 mb-2">
            Company
          </label>
          <input
            id="company"
            v-model="form.company"
            type="text"
            class="form-input"
            placeholder="Enter your company name"
            :disabled="isLoading"
          />
        </div>
        
        <!-- Interest Field -->
        <div>
          <label for="interest" class="block text-lg font-medium text-gray-700 mb-2">
            Interest
          </label>
          <select
            id="interest"
            v-model="form.interest"
            class="form-select"
            :disabled="isLoading"
          >
            <option value="">Select your interest</option>
            <option value="Product Demo">Product Demo</option>
            <option value="Pricing">Pricing</option>
            <option value="Partnership">Partnership</option>
            <option value="General Inquiry">General Inquiry</option>
            <option value="Other">Other</option>
          </select>
        </div>
        
        <!-- Notes Field -->
        <div>
          <label for="notes" class="block text-lg font-medium text-gray-700 mb-2">
            Notes
          </label>
          <textarea
            id="notes"
            v-model="form.notes"
            rows="3"
            class="form-textarea"
            placeholder="Any additional information..."
            :disabled="isLoading"
          ></textarea>
        </div>
        
        <!-- Submit Button -->
        <button
          type="submit"
          class="btn-primary w-full"
          :disabled="isLoading"
        >
          <span v-if="isLoading" class="flex items-center justify-center">
            <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Submitting...
          </span>
          <span v-else>Submit Lead</span>
        </button>
      </form>
      
      <!-- Success Message -->
      <div v-if="showSuccess" class="mt-6 bg-green-50 border border-green-200 rounded-lg p-4 text-center">
        <div class="text-green-800 font-medium text-lg mb-2">✅ Lead Submitted Successfully!</div>
        <p class="text-green-600">Thank you for your interest. We'll be in touch soon!</p>
      </div>
      
      <!-- Error Message -->
      <div v-if="error" class="mt-6 bg-red-50 border border-red-200 rounded-lg p-4 text-center">
        <div class="text-red-800 font-medium text-lg mb-2">❌ Error</div>
        <p class="text-red-600">{{ error }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useLeadStore } from '../stores/leadStore'

// Props
const props = defineProps({
  clientSlug: {
    type: String,
    required: true
  },
  showSlug: {
    type: String,
    required: true
  }
})

// Reactive data
const form = reactive({
  name: '',
  email: '',
  company: '',
  interest: '',
  notes: ''
})

const isLoading = ref(false)
const showSuccess = ref(false)
const error = ref('')

// Store
const leadStore = useLeadStore()

// Route
const route = useRoute()

// Methods
const resetForm = () => {
  form.name = ''
  form.email = ''
  form.company = ''
  form.interest = ''
  form.notes = ''
}

const submitLead = async () => {
  isLoading.value = true
  error.value = ''
  
  try {
    const leadData = {
      name: form.name.trim(),
      email: form.email.trim().toLowerCase(),
      company: form.company.trim(),
      interest: form.interest,
      notes: form.notes.trim()
    }
    
    // Submit lead using store
    await leadStore.submitLead(leadData, props.clientSlug, props.showSlug)
    
    // Show success message
    showSuccess.value = true
    resetForm()
    
    // Hide success message after 3 seconds
    setTimeout(() => {
      showSuccess.value = false
    }, 3000)
    
  } catch (err) {
    error.value = err.message || 'Failed to submit lead. Please try again.'
  } finally {
    isLoading.value = false
  }
}

// Lifecycle
onMounted(() => {
  // Sync any pending leads when component mounts
  leadStore.syncPendingLeads()
})
</script> 